import React, { useState } from "react";
import styled from "styled-components";
import i18n from "i18next";
import { useSelector, useDispatch } from "react-redux";
import { DialogOverlay, Dialog } from "components/dialog/DialogOverlay";
import ToggleSwitch from "components/inputs/ToggleSwitch";
import { allIndicesSelector, supportedIndicesSelector, updateSupportedIndexes } from "@kubera/common";

const MAX_SELECTED = 6;

const StyledDialog = styled(Dialog)`
  width: 500px;
  display: flex;
  margin-top: 80px;
  justify-content: center;
`;

const Container = styled.div`
  display: flex;
  margin: 60px;
  flex-direction: column;
  justify-content: flex-start;
  align-items: left;
  flex: 1;
`;

const Title = styled.div`
  font-style: normal;
  font-weight: bold;
  font-size: 22px;
  line-height: 130%;
  font-feature-settings: "ss01" on;
  font-weight: 700;
`;

const Description = styled.div`
  font-weight: 400;
  font-size: 12px;
  line-height: 150%;
  letter-spacing: 0%;
  opacity: 0.6;
  margin-bottom: 30px;
`;

const EmailLink = styled.a`
  color: ${props => props.theme.linkColor};
  text-decoration: underline;
`;

const List = styled.div`
  margin-top: 10px;
  display: flex;
  flex-direction: column;
`;

const ListItem = styled.div`
  margin-bottom: 18px;
`;

const SwitchItem = styled.label`
  display: inline-flex;
  align-items: center;
  cursor: pointer;
`;

const ItemLabel = styled.div`
  margin-left: 7px;
  font-weight: 400;
  font-size: 14px;
  line-height: 150%;
  letter-spacing: 0%;
`;

const BenchmarksDialog = ({ onDismiss }) => {
  const dispatch = useDispatch();
  const allIndices = useSelector(allIndicesSelector);
  const selectedIndices = useSelector(supportedIndicesSelector);
  const selectedIds = selectedIndices.map(idx => idx.id);

  const [selected, setSelected] = useState(selectedIds);

  const handleToggle = id => {
    if (selected.includes(id)) {
      setSelected(selected.filter(item => item !== id));
    } else {
      if (selected.length < MAX_SELECTED) {
        setSelected([...selected, id]);
      }
    }
  };

  const handleDialogDismiss = () => {
    const initialSet = new Set(selectedIds);
    const selectedSet = new Set(selected);
    const isSame = initialSet.size === selectedSet.size && [...initialSet].every(x => selectedSet.has(x));
    if (!isSame) {
      const selectedObjects = allIndices
        .filter(index => selected.includes(index.ticker))
        .map(index => ({ ...index, id: index.ticker }));
      dispatch(updateSupportedIndexes(selectedObjects));
      if (onDismiss) {
        onDismiss(selectedObjects.map(item => item.id));
      }
    } else {
      if (onDismiss) {
        onDismiss(null);
      }
    }
  };

  return (
    <DialogOverlay onDismiss={handleDialogDismiss}>
      <StyledDialog>
        <Container>
          <Title>{i18n.t("benchmarksDialog.title")}</Title>
          <Description
            dangerouslySetInnerHTML={{
              __html: i18n.t("benchmarksDialog.desc")
            }}
          />
          <List>
            {allIndices.map(index => {
              const isChecked = selected.includes(index.ticker);
              const isDisabled = !isChecked && selected.length >= MAX_SELECTED;
              return (
                <ListItem key={index.ticker}>
                  <SwitchItem>
                    <ToggleSwitch
                      isChecked={isChecked}
                      disabled={isDisabled}
                      onChange={() => handleToggle(index.ticker)}
                    />
                    <ItemLabel>{index.name}</ItemLabel>
                  </SwitchItem>
                </ListItem>
              );
            })}
          </List>
        </Container>
      </StyledDialog>
    </DialogOverlay>
  );
};

export default BenchmarksDialog;
